import { Link } from "gatsby";
import React, { FC, useCallback } from "react";

import { useDataLayer } from "../../../hooks/use-data-layer";
import { useFormattedDate } from "../../../hooks/use-formatted-date";
import { GoogleAnalyticsEvent } from "../../../utils/analytics";
import { Button } from "../../buttons/button";
import { JobData } from "../types";
import { getJobTitle } from "../utils";

export interface JobCardProps {
  job: JobData;
  detailLink?: string;
  labels: Record<string, string | null>;
}

export const JobCard: FC<JobCardProps> = ({ job, detailLink, labels }) => {
  const publishedAt = useFormattedDate(job.createdDateTime, { dateStyle: "short" });

  const { pushEvent } = useDataLayer();
  const onApplyJob = useCallback(() => {
    pushEvent(
      {
        event: GoogleAnalyticsEvent.JobApplication,
        job: job.jobId,
      },
      ["job"]
    );
  }, [pushEvent, job]);

  return (
    <div className="p-5 pt-7 bg-light overflow-hidden rounded-3xl flex flex-col gap-4">
      <h3 className="text-2xl xl:text-3xl break-words text-primary text-ellipsis line-clamp-3">
        {getJobTitle(job)}
      </h3>
      <div className="font-light text-sm xl:text-base !leading-loose">
        {job.macrofunction && <p className="font-bold uppercase mb-2">{job.macrofunction}</p>}
        <p>
          {labels["job-card-location"]}: {job.country}
        </p>
        <p>
          {labels["job-card-published-at"]}: {publishedAt}
        </p>
        <p>
          {labels["job-card-id"]}: {job.jobId}
        </p>
      </div>
      <div className="flex gap-4 lg:gap-6 mt-auto">
        <Link to={detailLink ?? ""} className="flex-1">
          <Button size="sm" variant="primary-outline" type="button" fullWidth>
            {labels["job-card-details"]}
          </Button>
        </Link>
        <a href={job.applicationLink} target="__blank" className="flex-1">
          <Button onClick={onApplyJob} size="sm" variant="primary" type="button" fullWidth>
            {labels["job-card-apply"]}
          </Button>
        </a>
      </div>
    </div>
  );
};
